import React, { useCallback, useEffect, useState, useRef, lazy } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { useFadeIn } from '../hooks/useFadeIn';
const PREVIEW_PAGES = [{
  src: 'https://cdn.magicpatterns.com/uploads/1bgyXEudYAnSWJL7zUBoDS/volume_1_(6).webp',
  alt: 'Find the Target — Volume 1: Attention & Focus activity page'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/fb8WvxvqbFrTJbuqm6n1wV/volume_2_(10).webp',
  alt: 'Visual Recall Grid — Volume 2: Memory Booster activity page'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/fVFwXqvKo1TEjuys14YFNz/volume_3_(14).webp',
  alt: 'Understanding Relationships — Volume 3: Learning Skills activity page'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/8h2aqN6KefnPZEyFBf67bU/volume_5_(8).webp',
  alt: 'The Waiting Game — Volume 5: Calm & Self-Control activity page'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/cWJaQRbeiD2sZiBjXz8AKd/volume_6_(19).webp',
  alt: 'The Big Search Challenge — Volume 6: Brain Power activity page'
}];
export function WhatsInsideSection() {
  const fadeIn = useFadeIn();
  const [current, setCurrent] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const currentRef = useRef(0);
  const total = PREVIEW_PAGES.length;
  const goTo = useCallback((index: number) => {
    const next = (index % total + total) % total;
    setCurrent(next);
    currentRef.current = next;
    if (scrollRef.current) {
      const child = scrollRef.current.children[next] as HTMLElement;
      if (child) {
        scrollRef.current.scrollTo({
          left: child.offsetLeft - scrollRef.current.offsetLeft,
          behavior: 'smooth'
        });
      }
    }
  }, [total]);
  const prev = () => goTo(current - 1);
  const next = () => goTo(current + 1);
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrollLeft = container.scrollLeft;
          const width = container.offsetWidth;
          const index = Math.round(scrollLeft / width);
          if (index !== currentRef.current && index >= 0 && index < total) {
            currentRef.current = index;
            setCurrent(index);
          }
          ticking = false;
        });
        ticking = true;
      }
    };
    container.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [total]);
  return <section className="py-10 md:py-16 px-4 bg-white" style={{
    contain: 'layout style'
  }} data-id="element-287">
      <div className="max-w-3xl mx-auto" data-id="element-288">
        <div ref={fadeIn.ref} className={fadeIn.className} data-id="element-289">
          <div className="text-center mb-6 md:mb-10" data-id="element-290">
            <h2 className="text-2xl md:text-5xl font-black text-gray-900 mb-3 md:mb-4" data-id="element-291">
              👀 Take A Look Inside
            </h2>
            <p className="text-sm md:text-lg text-gray-500 font-medium" data-id="element-292">
              Real activity pages from The Little Genius System™ (Printable
              Digital PDFs)
            </p>
          </div>

          <div className="relative" data-id="element-293">
            {/* Desktop arrows */}
            <button onClick={prev} className="hidden md:flex absolute -left-12 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white border border-gray-200 rounded-full items-center justify-center shadow-md hover:bg-gray-50 transition-colors cursor-pointer" aria-label="Previous" data-id="element-294">
              <ChevronLeftIcon className="w-5 h-5 text-gray-700" data-id="element-295" />
            </button>
            <button onClick={next} className="hidden md:flex absolute -right-12 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white border border-gray-200 rounded-full items-center justify-center shadow-md hover:bg-gray-50 transition-colors cursor-pointer" aria-label="Next" data-id="element-296">
              <ChevronRightIcon className="w-5 h-5 text-gray-700" data-id="element-297" />
            </button>

            {/* Carousel container */}
            <div ref={scrollRef} className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4" style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            WebkitOverflowScrolling: 'touch'
          }} data-id="element-298">
              {PREVIEW_PAGES.map((page, i) => <div key={i} className="flex-shrink-0 w-[85%] md:w-full snap-center" data-id="element-299">
                  <div className="bg-gray-50 rounded-2xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-300">
                    <img src={page.src} alt={page.alt} className="w-full h-auto object-contain" loading="lazy" decoding="async" width={800} height={1000} sizes="(max-width: 768px) 85vw, 700px" data-id="element-301" />
                  </div>
                  <p className="text-center text-xs md:text-sm text-gray-400 font-medium mt-2" data-id="element-302">
                    {page.alt}
                  </p>
                </div>)}
            </div>

            {/* Dot indicators */}
            <div className="flex items-center justify-center gap-2 mt-4" data-id="element-303">
              {PREVIEW_PAGES.map((_, i) => <button key={i} onClick={() => goTo(i)} className={`w-2.5 h-2.5 rounded-full cursor-pointer ${i === current ? 'bg-blue-600 w-6' : 'bg-gray-300'}`} aria-label={`Go to page ${i + 1}`} data-id="element-304" />)}
            </div>

            {/* Mobile swipe hint */}
            <p className="text-center text-[10px] text-gray-400 mt-2 md:hidden" data-id="element-305">
              ← Swipe to see more →
            </p>
          </div>
        </div>
      </div>
    </section>;
}